<template>
    <v-card class="mb-5 pa-2">
        <card-header
            :title="
                $t('modules.form.texts.title', {
                    'title-prefix': titlePrefix,
                })
            "
        />

        <v-form ref="form" @submit.prevent="save">
            <v-card-text>
                <v-row>
                    <v-col cols="12" xs="12" sm="12" md="4">
                        <v-text-field
                            v-model="model.plan.name"
                            disabled
                            :label="$t('plans.form.labels.plan-name')"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="5">
                        <v-text-field
                            v-model="model.name"
                            disabled
                            :label="$t('modules.form.labels.name')"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="3">
                        <v-currency-field
                            disabled
                            :label="$t('modules.form.labels.original-price')"
                            v-bind="$t('money-config')"
                            v-model="model.original_price"
                        ></v-currency-field>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" xs="12" sm="12" md="4">
                        <v-select
                            v-model="model.type"
                            :items="$t('modules.form.fields.type.values')"
                            :label="$t('modules.form.labels.type')"
                            :placeholder="$t('modules.form.placeholders.type')"
                            :error-messages="TypeErrors"
                            @input="$v.model.type.$touch()"
                            @blur="$v.model.type.$touch()"
                            clearable
                        ></v-select>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="4">
                        <v-currency-field
                            :label="$t('modules.form.labels.partner-price')"
                            v-bind="$t('money-config')"
                            v-model="model.partner_price"
                            :error-messages="PartnerPriceErrors"
                            @input="$v.model.partner_price.$touch()"
                            @blur="$v.model.partner_price.$touch()"
                        ></v-currency-field>
                    </v-col>

                    <v-col cols="12" xs="12" sm="12" md="4">
                        <v-currency-field
                            :label="$t('modules.form.labels.whitelabel-price')"
                            v-bind="$t('money-config')"
                            v-model="model.whitelabel_price"
                            :error-messages="WhitelabelPriceErrors"
                            @input="$v.model.whitelabel_price.$touch()"
                            @blur="$v.model.whitelabel_price.$touch()"
                        ></v-currency-field>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" xs="12" sm="12" md="4">
                        <v-text-field
                            :label="$t('modules.form.labels.included')"
                            v-model="model.included"
                            type="number"
                            :error-messages="IncludedErrors"
                            @input="$v.model.included.$touch()"
                            @blur="$v.model.included.$touch()"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="4">
                        <v-text-field
                            :label="$t('modules.form.labels.charge-from')"
                            v-model="model.charge_from"
                            type="number"
                            :error-messages="ChargeFromErrors"
                            @input="$v.model.charge_from.$touch()"
                            @blur="$v.model.charge_from.$touch()"
                        ></v-text-field>
                    </v-col>
                </v-row>
            </v-card-text>

            <form-actions :loadingSave="loading" />
        </v-form>
    </v-card>
</template>

<script>
import CardHeader from '@/components/utils/CardHeader'
import FormActions from '@/components/utils/FormActions'
import BaseModel from '@/models/BaseModel'
import { required, between, requiredIf } from 'vuelidate/lib/validators'
import { validationMixin } from 'vuelidate'
import { mask } from 'vue-the-mask'

export default {
    props: {
        module: {
            type: Object,
            required: true,
            validator: model => {
                return model instanceof BaseModel
            },
        },
        isEdit: {
            type: Boolean,
            default: () => {
                return false
            },
        },
        titlePrefix: String,
    },

    directives: { mask },

    data: () => {
        return {
            loading: false,
            allModules: [],
            updateOnPlans: false,
        }
    },

    components: {
        'card-header': CardHeader,
        'form-actions': FormActions,
    },

    mixins: [validationMixin],

    validations: {
        model: {
            partner_price: {
                required,
                between: between(0, 99999999.99),
            },
            whitelabel_price: {
                requiredIf: requiredIf(function(model) {
                    return model.whitelabel
                }),
                between: between(0, 99999999.99),
            },
            type: { required },
            included: { between: between(0, 100) },
            charge_from: { between: between(0, 101) },
        },
    },

    methods: {
        async save() {
            this.loading = true

            this.$v.$touch()
            if (this.$v.$invalid) {
                this.$bus.$emit(
                    'notify',
                    'error',
                    this.$t('form.validation-error'),
                )

                this.loading = false
                return
            }

            let clone = this.model.clone()

            let res = null

            try {
                res = await this.$http.put(
                    `config-plans/${this.module.plan.id}/module/${this.module.id}`,
                    clone,
                )
            } finally {
                this.loading = false
            }

            if (!res) return

            this.$emit('saved')

            this.$bus.$emit(
                'notify',
                'success',
                this.$t('modules.form.feedback.success', { name: clone.name }),
            )
        },

        clear() {
            this.$refs.form.reset()
        },
    },

    computed: {
        model() {
            return this.module
        },

        TypeErrors() {
            const errors = []
            if (!this.$v.model.type.$dirty) return errors

            !this.$v.model.type.required &&
                errors.push(this.$t('modules.form.errors.type.required'))

            return errors
        },
        PartnerPriceErrors() {
            const errors = []
            if (!this.$v.model.partner_price.$dirty) return errors

            !this.$v.model.partner_price.required &&
                errors.push(
                    this.$t('modules.form.errors.partner-price.required'),
                )

            !this.$v.model.partner_price.between &&
                errors.push(
                    this.$t('modules.form.errors.partner-price.between'),
                )

            return errors
        },
        WhitelabelPriceErrors() {
            const errors = []
            if (!this.$v.model.whitelabel_price.$dirty) return errors

            !this.$v.model.whitelabel_price.requiredIf &&
                errors.push(
                    this.$t('modules.form.errors.whitelabel-price.required'),
                )

            !this.$v.model.whitelabel_price.between &&
                errors.push(
                    this.$t('modules.form.errors.whitelabel-price.between'),
                )

            return errors
        },
        IncludedErrors() {
            const errors = []
            if (!this.$v.model.included.$dirty) return errors

            !this.$v.model.included.between &&
                errors.push(this.$t('modules.form.errors.included.between'))

            return errors
        },
        ChargeFromErrors() {
            const errors = []
            if (!this.$v.model.charge_from.$dirty) return errors

            !this.$v.model.charge_from.between &&
                errors.push(this.$t('modules.form.errors.charge_from.between'))

            return errors
        },
    },
}
</script>
